/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ENGINES, ARC_DATA, PILLARS, PILLAR_TO_ENGINES } from '../mock/mock';
import { api, useAuth } from './AuthContext';
import { localDateKey } from '../lib/dates';

const ArcContext = createContext(null);
const STORAGE_KEY = 'myarc_state_v2';
export const CORE_STREAK_PILLARS = ['hydration', 'movement', 'nutrition', 'sleep'];
export const CORE_STREAK_OPTION_GROUPS = [['reading', 'stillness']];

export const defaultState = {
  email: null,
  arcAssigned: false,
  engineKey: null,
  arcKey: null,
  assessmentScores: null,
  assessmentTaken: null,
  nickname: null,
  showBadgesInChambers: true,
  baselineRituals: null,
  baselineAssessmentTaken: null,
  baselineScore: 0,
  logs: [],
  protocol: {},
  zoneGates: { foundation_covered: false, emergency_3mo: false, first_productised: false, first_delegated_hire: false, asset_income_live: false },
  engineXP: { TITAN: 0, ARBITER: 0, ALCHEMIST: 0, ARTISAN: 0, ORACLE: 0, SOVEREIGN: 0 },
  engineTier: { TITAN: 0, ARBITER: 0, ALCHEMIST: 0, ARTISAN: 0, ORACLE: 0, SOVEREIGN: 0 },
  modulesDone: [],
  goals: [],
  strategyOutputs: [],
  financials: {
    expenses: { rent: 0, food: 0, transport: 0, utilities: 0, insurance: 0, subscriptions: 0, other: 0 },
    primaryIncome: 0,
    secondaryIncome: 0,
    surplusSplit: { reserve: 60, reinvest: 40 }
  },
  financialLog: [],   // [{ ym: 'YYYY-MM', foundation, income, ts }]
  badges: [],
  avatarBase64: null
};

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    return { ...defaultState, ...JSON.parse(raw) };
  } catch { return defaultState; }
}

function hasUsableArcState(candidate) {
  return Boolean(candidate?.arcAssigned && candidate?.arcKey && candidate?.engineKey);
}

function stateTime(value) {
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
}

function shouldKeepLocalArc(localState, serverState) {
  if (!hasUsableArcState(localState)) return false;
  if (!hasUsableArcState(serverState)) return true;
  return stateTime(localState.assessmentTaken) > stateTime(serverState.assessmentTaken);
}

function mergeKnownState(data, email) {
  const d = defaultState;
  return {
    ...d,
    ...data,
    email: email || data.email || null,
    financials: {
      ...d.financials,
      ...(data.financials || {}),
      expenses: { ...d.financials.expenses, ...((data.financials && data.financials.expenses) || {}) },
      surplusSplit: { ...d.financials.surplusSplit, ...((data.financials && data.financials.surplusSplit) || {}) },
    },
    zoneGates: { ...d.zoneGates, ...(data.zoneGates || {}) },
    engineXP: { ...d.engineXP, ...(data.engineXP || {}) },
    engineTier: { ...d.engineTier, ...(data.engineTier || {}) },
  };
}

function sameJson(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function changedObjectEntries(previous = {}, current = {}) {
  return Object.fromEntries(
    Object.keys(current || {})
      .filter(key => !sameJson(previous?.[key], current?.[key]))
      .map(key => [key, current[key]])
  );
}

function changedNestedObjectEntries(previous = {}, current = {}) {
  return Object.fromEntries(
    Object.keys(current || {})
      .map(key => {
        const previousValue = previous?.[key];
        const currentValue = current[key];
        if (
          previousValue &&
          currentValue &&
          typeof previousValue === 'object' &&
          typeof currentValue === 'object' &&
          !Array.isArray(previousValue) &&
          !Array.isArray(currentValue)
        ) {
          return [key, changedObjectEntries(previousValue, currentValue)];
        }
        return !sameJson(previousValue, currentValue) ? [key, currentValue] : [key, undefined];
      })
      .filter(([, value]) => value !== undefined && (!value || typeof value !== 'object' || Object.keys(value).length > 0))
  );
}

function itemKey(field, item) {
  if (field === 'modulesDone') return item;
  if (!item || typeof item !== 'object') return null;
  if (field === 'logs') return item.date || null;
  if (field === 'financialLog') return item.date || item.ym || null;
  return item.id || null;
}

function changedArrayItems(field, previous = [], current = []) {
  const previousByKey = new Map();
  (previous || []).forEach(item => {
    const key = itemKey(field, item);
    if (key) previousByKey.set(key, item);
  });
  return (current || []).filter(item => {
    const key = itemKey(field, item);
    if (!key) return true;
    return !sameJson(previousByKey.get(key), item);
  });
}

export function createStatePatch(previous = {}, current = {}) {
  const patch = {};
  const nestedObjectMergeFields = new Set(['protocol', 'financials']);
  const objectMergeFields = new Set(['zoneGates', 'engineXP', 'engineTier']);
  const arrayMergeFields = new Set(['logs', 'goals', 'financialLog', 'badges', 'strategyOutputs', 'modulesDone']);

  Object.keys(current || {}).forEach(key => {
    if (key === 'email') return;
    if (sameJson(previous?.[key], current[key])) return;

    if (nestedObjectMergeFields.has(key)) {
      const value = changedNestedObjectEntries(previous?.[key], current[key]);
      if (Object.keys(value).length > 0) patch[key] = value;
      return;
    }

    if (objectMergeFields.has(key)) {
      const value = changedObjectEntries(previous?.[key], current[key]);
      if (Object.keys(value).length > 0) patch[key] = value;
      return;
    }

    if (arrayMergeFields.has(key)) {
      const value = changedArrayItems(key, previous?.[key], current[key]);
      if (value.length > 0) patch[key] = value;
      return;
    }

    patch[key] = current[key];
  });

  return patch;
}

function addBadge(badges, id, name) {
  const current = badges || [];
  return current.find(b => b.id === id)
    ? current
    : [...current, { id, name, earnedAt: new Date().toISOString() }];
}

export function isProtocolDateEditable(date) {
  return date === localDateKey();
}

export function getCoreStreakStatus(day = {}) {
  const hasAction = id => Number(day?.[id] || 0) > 0;
  const required = CORE_STREAK_PILLARS.map(id => ({ id, done: hasAction(id) }));
  const optionGroups = CORE_STREAK_OPTION_GROUPS.map(ids => ({
    ids,
    done: ids.some(hasAction),
  }));
  const completedSlots = required.filter(item => item.done).length + optionGroups.filter(group => group.done).length;
  const totalSlots = required.length + optionGroups.length;
  return {
    required,
    optionGroups,
    completedSlots,
    totalSlots,
    complete: completedSlots === totalSlots,
  };
}

export function isCoreStreakDay(protocol, date) {
  return getCoreStreakStatus(protocol?.[date] || {}).complete;
}

export function ArcProvider({ children }) {
  const [state, setState] = useState(loadState);
  const [hydrated, setHydrated] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const lastSavedRef = useRef('');
  const hydratedRef = useRef(false);

  // Hydrate from backend when user logs in
  useEffect(() => {
    if (authLoading) return;
    if (!user) { hydratedRef.current = false; setHydrated(true); return; }
    setHydrated(false);
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/state');
        if (cancelled) return;
        if (r.data && Object.keys(r.data).length > 0) {
          // Shallow merge would let an empty {} from the backend wipe out
          // nested defaults (e.g. financials.expenses). Deep-merge known
          // nested object fields so missing server values fall back to the
          // default shape and the dashboard can't crash on them.
          const merged = mergeKnownState(r.data, user.email);
          lastSavedRef.current = JSON.stringify(merged);
          setState(prev => shouldKeepLocalArc(prev, merged) ? { ...merged, ...prev, email: user.email || prev.email || null } : merged);
        } else {
          const fresh = { ...defaultState, email: user.email || null };
          lastSavedRef.current = JSON.stringify(fresh);
          setState(prev => shouldKeepLocalArc(prev, fresh) ? { ...prev, email: user.email || prev.email || null } : fresh);
        }
        hydratedRef.current = true;
      } catch {
        hydratedRef.current = false;
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // Persist locally + debounced push to server when authed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    if (!user || !hydratedRef.current) return;
    const serialised = JSON.stringify(state);
    if (serialised === lastSavedRef.current) return;
    let previous = defaultState;
    try { previous = JSON.parse(lastSavedRef.current || '{}'); } catch { previous = defaultState; }
    const patch = createStatePatch(previous, state);
    if (Object.keys(patch).length === 0) return;
    const t = setTimeout(async () => {
      try {
        await api.put('/state', patch);
        lastSavedRef.current = serialised;
      } catch { /* offline tolerant */ }
    }, 600);
    return () => clearTimeout(t);
  }, [state, user]);

  const engine = state.engineKey ? ENGINES[state.engineKey] : null;
  const arcKey = state.arcKey;
  const arc = arcKey && ARC_DATA[arcKey] ? { key: arcKey, ...ARC_DATA[arcKey] } : null;

  const update = (patch) => setState(prev => ({ ...prev, ...patch }));
  const replaceState = (nextState) => setState({ ...defaultState, ...nextState });

  const earnBadge = (id, name) => setState(prev => prev.badges.find(b => b.id === id) ? prev : ({
    ...prev, badges: [...prev.badges, { id, name, earnedAt: new Date().toISOString() }]
  }));

  const setBaseline = (rituals, financeSetup = null) => {
    const yes = Object.values(rituals).filter(Boolean).length;
    const score = 60 + yes * 8;
    const cleanNumber = value => {
      const next = Number(value || 0);
      return Number.isFinite(next) && next > 0 ? next : 0;
    };

    setState(prev => {
      const next = {
        ...prev,
        baselineRituals: rituals,
        baselineAssessmentTaken: prev.assessmentTaken || null,
        baselineScore: score,
      };
      const badges = prev.badges || [];
      const nextBadges = [...badges];
      const addBadge = (id, name) => {
        if (!nextBadges.find(b => b.id === id)) {
          nextBadges.push({ id, name, earnedAt: new Date().toISOString() });
        }
      };
      addBadge('baseline_set', 'Baseline Locked');

      if (financeSetup) {
        const defaultFinancials = defaultState.financials;
        const currentFinancials = prev.financials || defaultFinancials;
        const currentExpenses = currentFinancials.expenses || defaultFinancials.expenses;
        const inputExpenses = financeSetup.expenses || {};
        const expenses = Object.fromEntries(
          Object.keys(defaultFinancials.expenses).map(key => [key, cleanNumber(inputExpenses[key] ?? currentExpenses[key])])
        );
        const primaryIncome = cleanNumber(financeSetup.primaryIncome ?? currentFinancials.primaryIncome);
        const secondaryIncome = cleanNumber(financeSetup.secondaryIncome ?? currentFinancials.secondaryIncome);
        const hasFinance = Object.values(expenses).some(v => v > 0) || primaryIncome > 0 || secondaryIncome > 0;

        if (hasFinance) {
          const financials = {
            ...defaultFinancials,
            ...currentFinancials,
            expenses,
            primaryIncome,
            secondaryIncome,
            surplusSplit: currentFinancials.surplusSplit || defaultFinancials.surplusSplit,
          };
          const foundation = Object.values(expenses).reduce((sum, v) => sum + cleanNumber(v), 0);
          const income = primaryIncome + secondaryIncome;
          const date = localDateKey();
          const entry = { date, ym: date.slice(0, 7), foundation, income, ts: new Date().toISOString() };
          next.financials = financials;
          next.financialLog = [
            ...((prev.financialLog || []).filter(e => (e.date || e.ym) !== date)),
            entry,
          ].sort((a, b) => (a.date || a.ym || '').localeCompare(b.date || b.ym || ''));
          addBadge('financial_checkin', 'First Financial Check-in');
        }
      }

      next.badges = nextBadges;
      return next;
    });
  };

  const mergeProtocol = (date, pillarId, value) => {
    if (!isProtocolDateEditable(date)) return;
    setState(prev => {
      const protocol = { ...prev.protocol, [date]: { ...(prev.protocol[date] || {}), [pillarId]: Math.max(0, value) } };
      const streak = computeStreakState(protocol);
      return {
        ...prev,
        protocol,
        badges: streak.count >= 7 ? addBadge(prev.badges, 'week_streak', '7 Day Core Streak') : prev.badges,
      };
    });
  };

  const addNote = (note) => {
    const today = localDateKey();
    setState(prev => {
      const filtered = prev.logs.filter(l => l.date !== today);
      return { ...prev, logs: [...filtered, { date: today, note }] };
    });
  };

  const completeModule = (engineKey, moduleId, xp) => {
    setState(prev => {
      if (prev.modulesDone.includes(moduleId)) return prev;
      const nextXP = Math.min(100, (prev.engineXP[engineKey] || 0) + xp);
      const tierUp = nextXP >= 100;
      return {
        ...prev,
        modulesDone: [...prev.modulesDone, moduleId],
        engineXP: { ...prev.engineXP, [engineKey]: tierUp ? 0 : nextXP },
        engineTier: { ...prev.engineTier, [engineKey]: tierUp ? (prev.engineTier[engineKey] + 1) : prev.engineTier[engineKey] }
      };
    });
    earnBadge('first_module', 'First Module Cleared');
  };

  const addGoal = (goal) => setState(prev => ({ ...prev, goals: [goal, ...prev.goals] }));
  const updateGoal = (id, updater) => setState(prev => {
    let completedGoal = false;
    const goals = prev.goals.map(g => {
      if (g.id !== id) return g;
      const next = updater(g);
      completedGoal = Boolean(!g.completed && next.completed);
      return next;
    });
    return {
      ...prev,
      goals,
      badges: completedGoal ? addBadge(prev.badges, 'goal_built', 'First Life Goal Built') : prev.badges,
    };
  });
  const removeGoal = (id) => setState(prev => ({
    ...prev,
    goals: prev.goals.map(g => g.id === id ? { ...g, deleted: true, deletedAt: new Date().toISOString() } : g)
  }));

  const resetAll = () => { localStorage.removeItem(STORAGE_KEY); setState(defaultState); };

  const value = useMemo(() => ({
    state, engine, arc, hydrated, update, replaceState, mergeProtocol, addNote, addGoal, updateGoal, removeGoal,
    setBaseline, completeModule, earnBadge, resetAll
  }), [state, engine, arc, hydrated, update, replaceState, mergeProtocol, addNote, addGoal, updateGoal, removeGoal, setBaseline, completeModule, earnBadge, resetAll]);

  return <ArcContext.Provider value={value}>{children}</ArcContext.Provider>;
}

export const useArc = () => {
  const ctx = useContext(ArcContext);
  if (!ctx) throw new Error('useArc must be used inside ArcProvider');
  return ctx;
};

export function computeTrajectory(baselineScore, protocol) {
  const dates = Object.keys(protocol).sort();
  let score = baselineScore || 100;
  const series = [{ day: 0, score, date: 'baseline' }];
  dates.forEach((d, i) => {
    const p = protocol[d] || {};
    const pillarsDone = PILLARS.reduce((acc, pl) => acc + (p[pl.id] && p[pl.id] >= pl.target ? 1 : 0), 0);
    const ratio = pillarsDone / PILLARS.length;
    const factor = 1 + 0.005 + ratio * 0.04;
    score = score * factor;
    series.push({ day: i + 1, score, date: d, pillarsDone });
  });
  return { series, score: Math.round(score) };
}

export function computeStreak(protocol) {
  return computeStreakState(protocol).count;
}

// Full Golden-Streak state machine.
// Returns: { count, status: 'active'|'paused'|'recovering', graceDaysRemaining, recoveryDays, recoveryNeeded, recovered }
export function computeStreakState(protocol) {
  const GRACE_DAYS = 7;
  const RECOVERY_NEEDED = 3;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dateStr = (d) => localDateKey(d);
  const hasActivity = (d) => isCoreStreakDay(protocol, dateStr(d));

  // If today has activity, count consecutive days backwards.
  if (hasActivity(today)) {
    let count = 0;
    const d = new Date(today);
    while (hasActivity(d)) { count++; d.setDate(d.getDate() - 1); }
    return { count, status: 'active', graceDaysRemaining: null, recoveryDays: 0 };
  }

  // Today has no activity, find the last day that did.
  let lastActiveDate = null;
  for (let i = 1; i <= 400; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    if (hasActivity(d)) { lastActiveDate = d; break; }
  }

  if (!lastActiveDate) return { count: 0, status: 'active', graceDaysRemaining: null, recoveryDays: 0 };

  const daysSinceActive = Math.floor((today - lastActiveDate) / 86400000);

  if (daysSinceActive > GRACE_DAYS) {
    // Grace window expired, full reset.
    return { count: 0, status: 'active', graceDaysRemaining: null, recoveryDays: 0 };
  }

  // Count the saved streak up to and including the last active day
  let savedStreak = 0;
  { const d = new Date(lastActiveDate); while (hasActivity(d)) { savedStreak++; d.setDate(d.getDate() - 1); } }

  const graceDaysRemaining = GRACE_DAYS - daysSinceActive + 1;

  // Count consecutive recovery days going backwards from yesterday (days after the gap that have activity)
  const gapStart = new Date(lastActiveDate);
  gapStart.setDate(gapStart.getDate() + 1);
  let recoveryDays = 0;
  {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const d = new Date(yesterday);
    while (d >= gapStart && hasActivity(d)) { recoveryDays++; d.setDate(d.getDate() - 1); }
  }

  if (recoveryDays >= RECOVERY_NEEDED) {
    // Recovery complete, add recovery days to saved streak.
    return { count: savedStreak + recoveryDays, status: 'active', graceDaysRemaining: null, recoveryDays, recovered: true };
  }
  if (recoveryDays > 0) {
    return { count: savedStreak, status: 'recovering', graceDaysRemaining, recoveryDays, recoveryNeeded: RECOVERY_NEEDED };
  }
  return { count: savedStreak, status: 'paused', graceDaysRemaining, recoveryDays: 0 };
}

// Engine progress = Quiz baseline + Protocol XP (capped at 100).
// Each day a pillar hits its daily target adds 1 XP to each engine it maps to (PILLAR_TO_ENGINES).
// This is what the Engine Tier Map renders. Top engine starts in the 70-85% range from the quiz,
// leaving real headroom for daily grind. Bottom engines start lower but grow the same way.
export function computeEngineProgress(state) {
  const baseline = state.assessmentScores || { TITAN:0, ARBITER:0, ALCHEMIST:0, ARTISAN:0, ORACLE:0, SOVEREIGN:0 };
  const protocolBoost = { TITAN:0, ARBITER:0, ALCHEMIST:0, ARTISAN:0, ORACLE:0, SOVEREIGN:0 };

  const pillarTargets = Object.fromEntries(PILLARS.map(p => [p.id, p.target]));
  const days = Object.values(state.protocol || {});
  for (const day of days) {
    for (const [pid, val] of Object.entries(day || {})) {
      const target = pillarTargets[pid] || 0;
      if (target <= 0) continue;
      if ((val || 0) >= target) {
        const engines = PILLAR_TO_ENGINES[pid] || [];
        for (const k of engines) protocolBoost[k] = (protocolBoost[k] || 0) + 1;
      }
    }
  }

  const out = {};
  for (const k of Object.keys(protocolBoost)) {
    const b = Math.max(0, Math.min(100, Math.round(baseline[k] || 0)));
    const x = Math.max(0, Math.round(protocolBoost[k]));
    const pct = Math.max(0, Math.min(100, b + x));
    out[k] = { baseline: b, protocolBoost: x, pct, ready: pct >= 100 };
  }
  return out;
}
